/**
 * Build 1 — locked invitation contract (GET preview + POST accept).
 *
 * `api` response interceptor already returns the payload (flat body, inner
 * envelope, or `{ __zephixInner, __zephixMeta }`). Callers must NOT read
 * AxiosResponse `.data` / `.status` on that value — that is
 * INVITE-ACCEPTANCE-INTERCEPTOR-CONTRACT-1 (preview rendered nothing on
 * staging because `raw.data` was undefined on a flat body).
 */
import type { AxiosAdapter } from "axios";
import { api, unwrapZephixClientPayload } from "@/lib/api";

/**
 * Test-only: pin an adapter on the same axios instance preview/accept use.
 * Installing on a different `api` import (Vite duplicate graph) hides this seam.
 */
export function attachInvitationApiAdapterForTests(adapter: AxiosAdapter): () => void {
  const previous = api.defaults.adapter;
  api.defaults.adapter = adapter;
  const interceptorId = api.interceptors.request.use((cfg) => {
    cfg.adapter = adapter;
    return cfg;
  });
  return () => {
    api.interceptors.request.eject(interceptorId);
    api.defaults.adapter = previous;
  };
}

export type InvitationPreview = {
  email: string;
  orgName: string;
  workspaceName?: string;
  invitedRole: string;
  expiresAt: string;
  /** true/false when a session is present. null means nobody is signed in — not a mismatch. */
  emailMatches: boolean | null;
};

function unwrap<T>(payload: unknown): T {
  return (unwrapZephixClientPayload(payload) ?? payload) as T;
}

export async function previewInvitation(token: string): Promise<InvitationPreview> {
  const payload = await api.get<unknown>(`/v1/invitations/${encodeURIComponent(token)}`);
  const raw = unwrap<Record<string, unknown>>(payload) ?? {};
  const matches = raw.emailMatches;
  return {
    email: String(raw.email ?? ""),
    orgName: String(raw.orgName ?? ""),
    workspaceName: raw.workspaceName != null ? String(raw.workspaceName) : undefined,
    invitedRole: String(raw.invitedRole ?? ""),
    expiresAt: String(raw.expiresAt ?? ""),
    emailMatches: matches === true ? true : matches === false ? false : null,
  };
}

export type AcceptInvitationResult =
  | { status: 200; data: { orgId: string; workspaceId?: string } }
  | {
      status: 201;
      data: {
        user: Record<string, unknown>;
        accessToken: string;
        refreshToken: string;
        sessionId?: string;
      };
    };

/**
 * POST accept. HTTP 200 vs 201 is not available after the interceptor.
 * Discriminate from the payload: a session (accessToken) means new-user 201;
 * orgId without tokens means existing-user 200.
 */
export async function acceptInvitation(
  token: string,
  body: Record<string, unknown>,
): Promise<AcceptInvitationResult> {
  const payload = await api.post<unknown>(
    `/v1/invitations/${encodeURIComponent(token)}/accept`,
    body,
  );
  const flat = unwrap<Record<string, unknown>>(payload) ?? {};
  const accessToken = String(flat.accessToken ?? flat.access_token ?? "");
  const refreshToken = String(flat.refreshToken ?? flat.refresh_token ?? "");
  if (accessToken.length > 0) {
    return {
      status: 201,
      data: {
        user: (flat.user as Record<string, unknown>) ?? {},
        accessToken,
        refreshToken,
        sessionId: typeof flat.sessionId === "string" ? flat.sessionId : undefined,
      },
    };
  }
  return {
    status: 200,
    data: {
      orgId: String(flat.orgId ?? flat.org_id ?? ""),
      workspaceId:
        flat.workspaceId != null
          ? String(flat.workspaceId)
          : flat.workspace_id != null
            ? String(flat.workspace_id)
            : undefined,
    },
  };
}
